import { type Storefront, detectStorefront } from '@/hooks/useStorefront';

/**
 * Payment providers supported by BigFollow.
 */
export type PaymentProvider = 'stripe' | 'twint';

/**
 * Single Source of Truth: which payment methods are available per storefront.
 *
 * CH keeps the exact today-order: ['twint', 'stripe'] — TWINT at index 0
 * remains the default and is rendered first, byte-identical to the legacy UI.
 * All other storefronts offer Stripe only.
 *
 * KEEP IN SYNC with any backend hard-locks (e.g. create-woo-order TWINT guard).
 */
export const PAYMENT_METHODS_BY_STOREFRONT: Record<Storefront, PaymentProvider[]> = {
  ch: ['twint', 'stripe'],
  de: ['stripe'],
  at: ['stripe'],
  it: ['stripe'],
  se: ['stripe'],
  al: ['stripe'],
};

/** Returns the ordered payment-method list for a storefront. */
export function paymentMethodsForStorefront(sf: Storefront): PaymentProvider[] {
  return PAYMENT_METHODS_BY_STOREFRONT[sf] ?? ['stripe'];
}

/**
 * Stripe Payment Method Configuration (PMC) per storefront.
 *
 * SSOT — MUST stay in sync with
 * `supabase/functions/_shared/payment-methods.ts` (`paymentMethodConfigurationFor`).
 * Frontend (`elements()`) and backend (PaymentIntent) MUST use the SAME id,
 * otherwise Stripe throws a mismatch error on confirm.
 *
 * CH: Karte, Link, Apple/Google Pay — kein PayPal, kein Klarna.
 * AT: + EPS, Klarna, PayPal — kein Bancontact.
 * IT: + Klarna, PayPal — kein EPS/Bancontact.
 * AL: Karte, Link, Apple/Google Pay, PayPal — kein Klarna/EPS/Bancontact.
 */
export const STRIPE_PMC_BY_STOREFRONT: Record<Storefront, string> = {
  ch: 'pmc_1U5mWhQkYAkmITqkUAQVf2IT',
  de: 'pmc_1U5mWhQkYAkmITqkUAQVf2IT',
  at: 'pmc_1U5mWiQkYAkmITqkf542X3xm',
  it: 'pmc_1U5mWiQkYAkmITqkv2dPV9V3',
  se: 'pmc_1U5mWiQkYAkmITqkv2dPV9V3',
  // Eigene AL-Konfiguration ("BigFollow AL", live, aktiv):
  // Karte, Link, Apple Pay, Google Pay, PayPal — Klarna/EPS/Bancontact off.
  al: 'pmc_1U5uypQkYAkmITqkFeNHdE1n',
};

/** Returns the Stripe Payment Method Configuration id for a storefront. */
export function stripePaymentMethodConfiguration(sf: Storefront): string {
  return STRIPE_PMC_BY_STOREFRONT[sf] ?? STRIPE_PMC_BY_STOREFRONT.ch;
}

/**
 * Storefront-level pin for the Stripe Elements UI language.
 * Only set where the storefront locale is NOT a Stripe-supported locale:
 * AL ships Albanian (sq) content, but Stripe has no `sq` UI — we pin `en`
 * deliberately instead of relying on the generic fallback chain.
 * CH/AT/IT/DE/SE return null → existing behaviour byte-identical.
 */
export const STRIPE_LOCALE_PIN: Partial<Record<Storefront, 'de' | 'en' | 'it' | 'fr'>> = {
  al: 'en',
};

export function stripeLocaleForStorefront(sf: Storefront): 'de' | 'en' | 'it' | 'fr' | null {
  return STRIPE_LOCALE_PIN[sf] ?? null;
}

/** International dialling prefix per storefront (phone hints / future inputs). */
export const PHONE_PREFIX_BY_STOREFRONT: Record<Storefront, string> = {
  ch: '+41', de: '+49', at: '+43', it: '+39', se: '+46', al: '+355',
};

/** Returns the default (first) payment method for a storefront. */
export function defaultPaymentMethod(sf: Storefront): PaymentProvider {
  return paymentMethodsForStorefront(sf)[0];
}

/** Convenience helper: default method for the currently detected storefront. */
export function defaultPaymentMethodForDetected(): PaymentProvider {
  return defaultPaymentMethod(detectStorefront());
}

/**
 * Human-readable label list of payment methods for a storefront, used by
 * checkout trust badges. Byte-identical to the legacy string for CH.
 */
export function paymentMethodsLabel(sf: Storefront): string {
  const parts = paymentMethodsForStorefront(sf).flatMap((p) => {
    if (p === 'twint') return ['Twint'];
    if (p === 'stripe') return ['Kredit-/Debitkarte', 'Apple Pay', 'Klarna', 'PayPal'];
    return [];
  });
  return parts.join(' · ');
}
